import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { 
  Trash2, Plus, Minus, ShoppingBag, ShieldCheck, Truck, 
  Clock, Lock, CreditCard, Zap, ChevronRight, Star, AlertTriangle, MessageCircle
} from 'lucide-react';
import { FreeDeliveryPill } from '../components/custom/AnnouncementBar';
import { ProductCard } from '../components/custom/ProductCard';
import { shareCartViaWhatsApp } from '../utils/whatsapp';
import api from '../utils/api';

function CartPage() {
  const { cart, cartTotal, updateQuantity, removeFromCart } = useCart();
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [showExitPopup, setShowExitPopup] = useState(false);

  // Fetch recommended products
  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        const data = await api.getProducts({ limit: 4 });
        // Filter out items already in cart
        const cartIds = cart.map(item => item.id);
        const filtered = (data.products || []).filter(p => !cartIds.includes(p.id));
        setRecommendations(filtered.slice(0, 4));
      } catch (error) {
        console.log('Failed to fetch recommendations');
      }
    };
    if (cart.length > 0) {
      fetchRecommendations();
    }
  }, [cart]);

  // Exit intent for cart page
  useEffect(() => {
    if (cart.length === 0) return;
    
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !sessionStorage.getItem('cart_exit_shown')) {
        setShowExitPopup(true);
        sessionStorage.setItem('cart_exit_shown', 'true');
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [cart.length]);

  // Calculate savings
  const originalTotal = cart.reduce((sum, item) => {
    const originalPrice = item.original_price || item.price;
    return sum + (originalPrice * item.quantity);
  }, 0);
  const totalSavings = originalTotal - cartTotal;

  if (cart.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="cart-page">
        <div className="text-center space-y-4">
          <ShoppingBag className="w-20 h-20 mx-auto text-muted-foreground" />
          <h2 className="text-2xl font-bold">Your cart is empty</h2>
          <p className="text-muted-foreground">Add some awesome products to your cart!</p>
          <Button onClick={() => navigate('/products')} size="lg" className="rounded-xl">
            Browse Products
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-3 sm:py-4 px-3 sm:px-4 pb-40 bg-gray-50" data-testid="cart-page">
      <div className="container mx-auto max-w-4xl">
        {/* Header with item count */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold">Shopping Cart ({cart.length})</h1>
          <Button variant="ghost" size="sm" onClick={() => navigate('/products')} className="text-xs sm:text-sm">
            Continue Shopping
          </Button>
        </div>

        {/* Urgency Banner */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <Clock className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-sm sm:text-base">Complete your order now!</p>
              <p className="text-xs sm:text-sm opacity-90">Items in cart are not reserved</p>
            </div>
          </div>
          <Badge className="bg-white text-orange-600 hover:bg-white text-[10px] sm:text-xs flex-shrink-0">
            <Zap className="w-3 h-3 mr-1" />
            Limited Stock
          </Badge>
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-3">
            {cart.map(item => (
              <Card 
                key={item.id} 
                className={`p-4 hover:shadow-md transition-shadow ${item.isBundle ? 'border-2 border-cyan-200 bg-gradient-to-br from-cyan-50/50 to-white' : ''}`} 
                data-testid="cart-item"
              >
                {item.isBundle && (
                  <Badge className="mb-2 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white">
                    <Star className="w-3 h-3 mr-1" />
                    Bundle Deal
                  </Badge>
                )}
                <div className="flex gap-3">
                  <div className="relative flex-shrink-0">
                    {item.isBundle && item.image_urls?.length > 1 ? (
                      <div className="grid grid-cols-2 gap-0.5 w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden">
                        {item.image_urls.slice(0, 4).map((url, idx) => (
                          <img
                            key={idx}
                            src={url || 'https://via.placeholder.com/50'}
                            alt={`${item.title} item ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        ))}
                      </div>
                    ) : (
                      <img
                        src={item.image_urls?.[0] || 'https://via.placeholder.com/100'}
                        alt={item.title}
                        className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg"
                      />
                    )}
                    {item.original_price && item.original_price > item.price && (
                      <Badge className="absolute -top-2 -right-2 bg-green-500 text-white text-[10px]">
                        Save ₹{(item.original_price - item.price) * item.quantity}
                      </Badge>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm sm:text-base line-clamp-2 mb-1">{item.title}</h3>
                    {item.isBundle && item.productCount && (
                      <p className="text-xs text-cyan-600 font-medium mb-1">
                        Contains {item.productCount} items
                      </p>
                    )}
                    <div className="flex items-baseline gap-2 mb-2">
                      <p className="text-base sm:text-lg font-bold text-cyan-600">₹{item.price}</p>
                      {item.original_price && item.original_price > item.price && (
                        <p className="text-xs sm:text-sm text-gray-400 line-through">₹{item.original_price}</p>
                      )}
                    </div>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="flex items-center gap-2 border rounded-lg bg-gray-50 w-fit">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          data-testid="decrease-quantity"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="w-8 text-center font-semibold" data-testid="cart-item-quantity">{item.quantity}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          data-testid="increase-quantity"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div className="flex items-center justify-between sm:gap-3">
                        <p className="font-bold text-base sm:text-lg">₹{(item.price * item.quantity).toFixed(0)}</p>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-gray-400 hover:text-destructive"
                          onClick={() => removeFromCart(item.id)}
                          data-testid="remove-from-cart"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3 py-4">
              <div className="flex flex-col items-center text-center p-2 sm:p-3 bg-white rounded-lg border">
                <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 mb-1" />
                <span className="text-[10px] sm:text-xs text-gray-600">100% Safe</span>
              </div>
              <div className="flex flex-col items-center text-center p-2 sm:p-3 bg-white rounded-lg border">
                <Truck className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 mb-1" />
                <span className="text-[10px] sm:text-xs text-gray-600">Free Delivery</span>
              </div>
              <div className="flex flex-col items-center text-center p-2 sm:p-3 bg-white rounded-lg border">
                <Lock className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 mb-1" />
                <span className="text-[10px] sm:text-xs text-gray-600">Secure Checkout</span>
              </div>
            </div>
          </div>

          {/* Order Summary - Sticky */}
          <div className="lg:col-span-1">
            <div className="sticky top-4">
              <Card className="p-5 border-2 border-primary/20">
                <h2 className="text-lg font-bold mb-4">Order Summary</h2>
                
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal ({cart.length} items)</span>
                    <span>₹{originalTotal.toFixed(0)}</span>
                  </div>
                  
                  {totalSavings > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount</span>
                      <span>-₹{totalSavings.toFixed(0)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Delivery</span>
                    <div className="flex items-center gap-2">
                      <span className="line-through text-gray-400 text-xs">₹50</span>
                      <FreeDeliveryPill />
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span data-testid="cart-total">₹{cartTotal.toFixed(0)}</span>
                  </div>
                  
                  {totalSavings > 0 && (
                    <div className="bg-green-50 text-green-700 text-sm p-2 rounded-lg text-center">
                      You're saving ₹{totalSavings.toFixed(0)} on this order!
                    </div>
                  )}
                </div>

                {/* Payment Methods Preview */}
                <div className="flex items-center justify-center gap-2 mt-4 py-2 border-y">
                  <CreditCard className="w-4 h-4 text-gray-400" />
                  <span className="text-xs text-gray-500">Cards, UPI, Wallets, COD accepted</span>
                </div>

                <Button
                  size="lg"
                  className="w-full mt-4 rounded-xl h-12 text-base bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600"
                  onClick={() => navigate('/checkout')}
                  data-testid="checkout-button"
                >
                  Proceed to Checkout
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Button>

                <Button
                  size="sm"
                  variant="outline"
                  className="w-full mt-2 gap-2"
                  onClick={() => window.open(shareCartViaWhatsApp(cart, cartTotal), '_blank')}
                >
                  <MessageCircle className="w-4 h-4" />
                  Share Cart via WhatsApp
                </Button>

                <p className="text-xs text-center text-gray-500 mt-3">
                  <Lock className="w-3 h-3 inline mr-1" />
                  Secure checkout powered by Razorpay
                </p>
              </Card>

              {/* Quick Stats */}
              <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm">
                <div className="flex items-center gap-2 text-blue-700">
                  <Star className="w-4 h-4" />
                  <span>Trusted by 1000+ happy parents</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* You Might Also Like */}
        {recommendations.length > 0 && (
          <div className="mt-10">
            <h2 className="text-xl font-bold mb-4">You Might Also Like</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {recommendations.map((product, idx) => (
                <ProductCard key={product.id} product={product} showBestseller={idx === 0} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sticky Mobile Checkout Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 lg:hidden z-40">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div>
            <p className="text-sm text-gray-600">{cart.length} items</p>
            <p className="text-xl font-bold">₹{cartTotal.toFixed(0)}</p>
          </div>
          <Button 
            size="lg" 
            className="rounded-xl px-8 bg-gradient-to-r from-orange-500 to-pink-500"
            onClick={() => navigate('/checkout')}
          >
            Checkout
            <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
        </div>
      </div>

      {/* Exit Intent Popup for Cart */}
      {showExitPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 relative animate-in zoom-in-95">
            <button 
              onClick={() => setShowExitPopup(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">Wait! Don't leave yet!</h3>
              <p className="text-gray-600 mb-4">
                Your cart items are waiting for you. Complete your order now before they run out!
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-600">Your cart total</p>
                <p className="text-2xl font-bold">₹{cartTotal.toFixed(0)}</p>
                {totalSavings > 0 && (
                  <p className="text-green-600 text-sm">Saving ₹{totalSavings.toFixed(0)}!</p>
                )}
              </div>

              <Button 
                className="w-full rounded-xl h-12 bg-gradient-to-r from-orange-500 to-pink-500"
                onClick={() => {
                  setShowExitPopup(false);
                  navigate('/checkout');
                }}
              >
                Complete My Order
              </Button>
              
              <button 
                onClick={() => setShowExitPopup(false)}
                className="text-sm text-gray-500 mt-3 hover:underline"
              >
                I'll come back later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CartPage;
